import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BookOpen, Plus, Edit, Trash2, Eye, Search, Filter,
  ChevronDown, BarChart3, Users, DollarSign, Calendar,
  CheckCircle, X, Star, Loader2, ExternalLink, Clock,
  TrendingUp, Rocket, Briefcase, MapPin, Tag, User, Linkedin, Upload,
  Quote, MessageSquare
} from 'lucide-react';
import FellowshipAdmin from './FellowshipAdmin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FutureSkillsAdmin = ({ token }) => {
  const [activeSubTab, setActiveSubTab] = useState('courses');
  const [courses, setCourses] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Testimonial state
  const [showTestimonialModal, setShowTestimonialModal] = useState(false);
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({
    name: '',
    photo_url: '',
    course: '',
    text: '',
    rating: null,
    package: '',
    role: '',
    is_active: true
  });
  
  // Form state
  const [courseForm, setCourseForm] = useState({
    title: '',
    slug: '',
    description: '',
    full_description: '',
    category: 'upskilling',
    price: 0,
    original_price: 0,
    duration_weeks: 8,
    mode: 'online',
    venue: '',
    venue_name: '',
    venue_address: '',
    start_date: '',
    end_date: '',
    max_seats: 50,
    is_featured: false,
    status: 'draft',
    learning_outcomes: [],
    requirements: [],
    skills: [],
    curriculum: [],
    instructors: [],
    // Existing fields
    coupons: [],
    mentor: null,
    // NEW: Projects (multiple)
    projects: [],
    min_projects_required: 2,
    // NEW: Certificates
    course_certificate: {
      title: '',
      description: '',
      preview_image: ''
    },
    industry_certificates: [],
    // NEW: Industry Partners
    industry_partners: [],
    // NEW: Confirmation Letter
    confirmation_letter_preview: '',
    // NEW: Impact Metrics
    show_impact_metrics: true,
    impact_metrics: {
      resume_shortlist_before: '12%',
      resume_shortlist_after: '65%+',
      interview_conversion_before: '8%',
      interview_conversion_after: '45%+',
      practical_experience_before: 'Low',
      practical_experience_after: 'High (Industry Projects)',
      recruiter_response_before: 'Minimal',
      recruiter_response_after: '3X Increase'
    },
    highlights: [
      '80%+ students reported improved interview confidence',
      'Top performers received internships & job opportunities'
    ]
  });
  
  const [newOutcome, setNewOutcome] = useState('');
  const [newRequirement, setNewRequirement] = useState('');
  const [newSkill, setNewSkill] = useState('');
  
  // Coupon form state
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discount_type: 'percentage',
    discount_value: 10,
    is_active: true
  });
  
  // Mentor form state
  const [mentorForm, setMentorForm] = useState({
    name: '',
    profile_image: '',
    experience: '',
    linkedin_url: ''
  });

  useEffect(() => {
    fetchData();
  }, [activeSubTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeSubTab === 'courses') {
        const res = await axios.get(`${API}/admin/future-skills/courses?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCourses(res.data.courses || []);
      } else if (activeSubTab === 'enrollments') {
        const res = await axios.get(`${API}/admin/future-skills/enrollments?limit=100`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEnrollments(res.data.enrollments || []);
      } else if (activeSubTab === 'analytics') {
        const res = await axios.get(`${API}/admin/future-skills/analytics`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAnalytics(res.data);
      } else if (activeSubTab === 'testimonials') {
        const res = await axios.get(`${API}/admin/future-skills/testimonials`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTestimonials(res.data.testimonials || []);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!courseForm.title || !courseForm.slug) {
      toast.error('Title and slug are required');
      return;
    }
    
    // Validate venue for offline mode
    if (courseForm.mode === 'offline') {
      if (!courseForm.venue_name || !courseForm.venue_address) {
        toast.error('Venue name and address are required for offline courses');
        return;
      }
    }
    
    // Prepare data with mentor info if provided
    const submitData = {
      ...courseForm,
      mentor: mentorForm.name ? mentorForm : null
    };
    
    try {
      if (editingCourse) {
        await axios.put(`${API}/admin/future-skills/courses/${editingCourse.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Course updated successfully');
      } else {
        await axios.post(`${API}/admin/future-skills/courses`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Course created successfully');
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save course');
    }
  };

  const handleDelete = async (courseId) => {
    if (!window.confirm('Are you sure you want to delete this course?')) return;
    
    try {
      await axios.delete(`${API}/admin/future-skills/courses/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Course deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete course');
    }
  };

  const resetForm = () => {
    setCourseForm({
      title: '',
      slug: '',
      description: '',
      full_description: '',
      category: 'upskilling',
      price: 0,
      original_price: 0,
      duration_weeks: 8,
      mode: 'online',
      venue: '',
      venue_name: '',
      venue_address: '',
      start_date: '',
      end_date: '',
      max_seats: 50,
      is_featured: false,
      status: 'draft',
      learning_outcomes: [],
      requirements: [],
      skills: [],
      curriculum: [],
      instructors: [],
      coupons: [],
      mentor: null,
      projects: [],
      min_projects_required: 2,
      course_certificate: { title: '', description: '', preview_image: '' },
      industry_certificates: [],
      industry_partners: [],
      confirmation_letter_preview: '',
      show_impact_metrics: true,
      impact_metrics: {
        resume_shortlist_before: '12%',
        resume_shortlist_after: '65%+',
        interview_conversion_before: '8%',
        interview_conversion_after: '45%+',
        practical_experience_before: 'Low',
        practical_experience_after: 'High (Industry Projects)',
        recruiter_response_before: 'Minimal',
        recruiter_response_after: '3X Increase'
      },
      highlights: [
        '80%+ students reported improved interview confidence',
        'Top performers received internships & job opportunities'
      ]
    });
    setMentorForm({
      name: '',
      profile_image: '',
      experience: '',
      linkedin_url: ''
    });
    setNewCoupon({
      code: '',
      discount_type: 'percentage',
      discount_value: 10,
      is_active: true
    });
    setEditingCourse(null);
  };

  const openEditModal = (course) => {
    setEditingCourse(course);
    setCourseForm({
      ...course,
      start_date: course.start_date ? course.start_date.split('T')[0] : '',
      end_date: course.end_date ? course.end_date.split('T')[0] : '',
      venue_name: course.venue_name || '',
      venue_address: course.venue_address || '',
      coupons: course.coupons || [],
      mentor: course.mentor || null,
      // New fields with defaults
      projects: course.projects || [],
      min_projects_required: course.min_projects_required || 2,
      course_certificate: course.course_certificate || { title: '', description: '', preview_image: '' },
      industry_certificates: course.industry_certificates || [],
      industry_partners: course.industry_partners || [],
      confirmation_letter_preview: course.confirmation_letter_preview || '',
      show_impact_metrics: course.show_impact_metrics !== false,
      impact_metrics: course.impact_metrics || {
        resume_shortlist_before: '12%',
        resume_shortlist_after: '65%+',
        interview_conversion_before: '8%',
        interview_conversion_after: '45%+',
        practical_experience_before: 'Low',
        practical_experience_after: 'High (Industry Projects)',
        recruiter_response_before: 'Minimal',
        recruiter_response_after: '3X Increase'
      },
      highlights: course.highlights || [
        '80%+ students reported improved interview confidence',
        'Top performers received internships & job opportunities'
      ]
    });
    // Load mentor form if exists
    if (course.mentor) {
      setMentorForm(course.mentor);
    } else {
      setMentorForm({
        name: '',
        profile_image: '',
        experience: '',
        linkedin_url: ''
      });
    }
    setShowModal(true);
  };

  const addToList = (field, value, setter) => {
    if (value.trim() && !courseForm[field].includes(value.trim())) {
      setCourseForm(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  const removeFromList = (field, index) => {
    setCourseForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(price);
  };

  const getCategoryIcon = (category) => {
    const icons = {
      summer_internship: Briefcase,
      placement_accelerator: Rocket,
      upskilling: TrendingUp
    };
    return icons[category] || BookOpen;
  };

  const getCategoryColor = (category) => {
    const colors = {
      summer_internship: 'amber',
      placement_accelerator: 'purple',
      upskilling: 'cyan'
    };
    return colors[category] || 'gray';
  };

  const filteredCourses = courses.filter(course => {
    if (searchQuery && !course.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    if (statusFilter && course.status !== statusFilter) return false;
    return true;
  });

  // Testimonial functions
  const resetTestimonialForm = () => {
    setTestimonialForm({
      name: '',
      photo_url: '',
      course: '',
      text: '',
      rating: null,
      package: '',
      role: '',
      is_active: true
    });
    setEditingTestimonial(null);
  };

  const openEditTestimonialModal = (testimonial) => {
    setEditingTestimonial(testimonial);
    setTestimonialForm({
      name: testimonial.name || '',
      photo_url: testimonial.photo_url || '',
      course: testimonial.course || '',
      text: testimonial.text || '',
      rating: testimonial.rating || null,
      package: testimonial.package || '',
      role: testimonial.role || '',
      is_active: testimonial.is_active !== false
    });
    setShowTestimonialModal(true);
  };

  const handleTestimonialSubmit = async (e) => {
    e.preventDefault();
    if (!testimonialForm.name || !testimonialForm.text) {
      toast.error('Name and testimonial text are required');
      return;
    }

    try {
      if (editingTestimonial) {
        await axios.put(
          `${API}/admin/future-skills/testimonials/${editingTestimonial.id}`,
          testimonialForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Testimonial updated');
      } else {
        await axios.post(
          `${API}/admin/future-skills/testimonials`,
          testimonialForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Testimonial created');
      }
      setShowTestimonialModal(false);
      resetTestimonialForm();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save testimonial');
    }
  };

  const handleDeleteTestimonial = async (testimonialId) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await axios.delete(`${API}/admin/future-skills/testimonials/${testimonialId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Testimonial deleted');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete testimonial');
    }
  };

  return (
    <div className="space-y-6">
      {/* Sub Navigation */}
      <div className="flex gap-2 border-b border-gray-700 pb-4">
        {[
          { id: 'courses', label: 'Courses', icon: BookOpen },
          { id: 'enrollments', label: 'Enrollments', icon: Users },
          { id: 'testimonials', label: 'Testimonials', icon: MessageSquare },
          { id: 'analytics', label: 'Analytics', icon: BarChart3 },
          { id: 'fellowship', label: 'Fellowship', icon: Rocket }
        ].map(tab => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveSubTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                activeSubTab === tab.id
                  ? 'bg-pink-500/20 text-pink-400 border border-pink-500/30'
                  : 'text-gray-400 hover:bg-gray-800'
              }`}
              data-testid={`subtab-${tab.id}`}
            >
              <Icon size={18} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Fellowship Tab (nested: Future-Skills → Fellowship) */}
      {activeSubTab === 'fellowship' && (
        <FellowshipAdmin token={token} />
      )}

      {/* Courses Tab */}
      {activeSubTab === 'courses' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={18} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search courses..."
                  className="pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-pink-500"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
              >
                <option value="">All Status</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              data-testid="create-course-btn"
            >
              <Plus size={18} />
              Create Course
            </button>
          </div>

          {/* Courses List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-pink-400" size={32} />
            </div>
          ) : filteredCourses.length === 0 ? (
            <div className="text-center py-12 bg-gray-800/50 rounded-xl">
              <BookOpen className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400">No courses found</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredCourses.map(course => {
                const Icon = getCategoryIcon(course.category);
                const color = getCategoryColor(course.category);
                return (
                  <div
                    key={course.id}
                    className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden hover:border-gray-600 transition-colors"
                    data-testid={`course-card-${course.id}`}
                  >
                    {/* Banner */}
                    <div className={`h-24 bg-gradient-to-br from-${color}-500/30 to-${color}-600/10 relative`}>
                      <div className="absolute top-3 left-3 flex gap-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          course.status === 'published' 
                            ? 'bg-green-500/20 text-green-400' 
                            : course.status === 'draft'
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {course.status}
                        </span>
                        {course.is_featured && (
                          <span className="px-2 py-1 bg-amber-500/20 text-amber-400 text-xs font-medium rounded-full flex items-center gap-1">
                            <Star size={10} />
                            Featured
                          </span>
                        )}
                      </div>
                      <div className={`absolute bottom-3 right-3 w-10 h-10 bg-${color}-500/20 rounded-lg flex items-center justify-center`}>
                        <Icon className={`text-${color}-400`} size={20} />
                      </div>
                    </div>
                    
                    {/* Content */}
                    <div className="p-4">
                      <h3 className="text-white font-semibold mb-1 line-clamp-1">{course.title}</h3>
                      <p className="text-gray-500 text-sm mb-3 line-clamp-2">{course.description}</p>
                      
                      <div className="flex items-center justify-between text-sm mb-4">
                        <span className="text-white font-bold">{formatPrice(course.price)}</span>
                        <span className="text-gray-500 flex items-center gap-1">
                          <Users size={14} />
                          {course.enrollment_count || 0} enrolled
                        </span>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex gap-2">
                        <a
                          href={`/future-skills/course/${course.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex-1 flex items-center justify-center gap-1 py-2 bg-gray-700 text-gray-300 rounded-lg text-sm hover:bg-gray-600 transition-colors"
                        >
                          <Eye size={14} />
                          View
                        </a>
                        <button
                          onClick={() => openEditModal(course)}
                          className="flex-1 flex items-center justify-center gap-1 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                        >
                          <Edit size={14} />
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(course.id)}
                          className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 transition-colors"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Enrollments Tab */}
      {activeSubTab === 'enrollments' && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-pink-400" size={32} />
            </div>
          ) : enrollments.length === 0 ? (
            <div className="text-center py-12 bg-gray-800/50 rounded-xl">
              <Users className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400">No enrollments yet</p>
            </div>
          ) : (
            <div className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Student</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-400">Course</th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-gray-400">Status</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400">Amount</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-gray-400">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {enrollments.map(enrollment => (
                    <tr key={enrollment.id} className="hover:bg-gray-800/50">
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white font-medium">{enrollment.student_name}</p>
                          <p className="text-gray-500 text-sm">{enrollment.student_email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-300 text-sm">{enrollment.course_title}</td>
                      <td className="px-4 py-3 text-center">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          enrollment.status === 'confirmed' 
                            ? 'bg-green-500/20 text-green-400' 
                            : enrollment.status === 'pending'
                            ? 'bg-amber-500/20 text-amber-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {enrollment.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right text-white font-medium">
                        {formatPrice(enrollment.amount_paid || 0)}
                      </td>
                      <td className="px-4 py-3 text-right text-gray-500 text-sm">
                        {new Date(enrollment.enrolled_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeSubTab === 'analytics' && (
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-pink-400" size={32} />
            </div>
          ) : analytics ? (
            <>
              {/* Stats Grid */}
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-pink-500/20 to-pink-600/10 border border-pink-500/30 rounded-xl p-5">
                  <BookOpen className="text-pink-400 mb-2" size={24} />
                  <p className="text-3xl font-bold text-white">{analytics.total_courses}</p>
                  <p className="text-gray-400 text-sm">Total Courses</p>
                </div>
                <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-xl p-5">
                  <CheckCircle className="text-purple-400 mb-2" size={24} />
                  <p className="text-3xl font-bold text-white">{analytics.published_courses}</p>
                  <p className="text-gray-400 text-sm">Published</p>
                </div>
                <div className="bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border border-cyan-500/30 rounded-xl p-5">
                  <Users className="text-cyan-400 mb-2" size={24} />
                  <p className="text-3xl font-bold text-white">{analytics.total_enrollments}</p>
                  <p className="text-gray-400 text-sm">Enrollments</p>
                </div>
                <div className="bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-xl p-5">
                  <DollarSign className="text-emerald-400 mb-2" size={24} />
                  <p className="text-3xl font-bold text-white">{formatPrice(analytics.total_revenue || 0)}</p>
                  <p className="text-gray-400 text-sm">Total Revenue</p>
                </div>
              </div>
              
              {/* Category Breakdown */}
              {analytics.by_category && analytics.by_category.length > 0 && (
                <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-white mb-4">Enrollments by Category</h3>
                  <div className="space-y-3">
                    {analytics.by_category.map((cat, idx) => {
                      const Icon = getCategoryIcon(cat._id);
                      const color = getCategoryColor(cat._id);
                      return (
                        <div key={idx} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 bg-${color}-500/20 rounded-lg flex items-center justify-center`}>
                              <Icon className={`text-${color}-400`} size={16} />
                            </div>
                            <span className="text-gray-300 capitalize">{cat._id?.replace('_', ' ')}</span>
                          </div>
                          <span className="text-white font-semibold">{cat.count} enrollments</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-12 bg-gray-800/50 rounded-xl">
              <BarChart3 className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400">No analytics data available</p>
            </div>
          )}
        </div>
      )}

      {/* Testimonials Tab */}
      {activeSubTab === 'testimonials' && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Manage Testimonials</h3>
              <p className="text-gray-400 text-sm">Add student success stories to display on the landing page</p>
            </div>
            <button
              onClick={() => { resetTestimonialForm(); setShowTestimonialModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
              data-testid="add-testimonial-btn"
            >
              <Plus size={18} />
              Add Testimonial
            </button>
          </div>

          {/* Testimonials List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-pink-400" size={32} />
            </div>
          ) : testimonials.length === 0 ? (
            <div className="text-center py-12 bg-gray-800/50 rounded-xl border border-gray-700">
              <Quote className="mx-auto text-gray-600 mb-4" size={48} />
              <p className="text-gray-400 mb-2">No testimonials yet</p>
              <p className="text-gray-500 text-sm">Add testimonials to display on the landing page</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {testimonials.map(testimonial => (
                <div
                  key={testimonial.id}
                  className={`bg-gray-800/50 border rounded-xl p-5 ${
                    testimonial.is_active ? 'border-gray-700' : 'border-red-500/30 opacity-60'
                  }`}
                  data-testid={`testimonial-card-${testimonial.id}`}
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      {testimonial.photo_url ? (
                        <img
                          src={testimonial.photo_url}
                          alt={testimonial.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                          {testimonial.name?.charAt(0) || 'T'}
                        </div>
                      )}
                      <div>
                        <p className="text-white font-semibold">{testimonial.name}</p>
                        {testimonial.role && (
                          <p className="text-cyan-400 text-sm">{testimonial.role}</p>
                        )}
                        {testimonial.course && (
                          <p className="text-gray-500 text-xs">{testimonial.course}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {!testimonial.is_active && (
                        <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full mr-2">
                          Inactive
                        </span>
                      )}
                      {testimonial.rating && (
                        <div className="flex items-center gap-1 px-2 py-1 bg-amber-500/20 rounded-full">
                          <Star size={12} className="text-amber-400 fill-amber-400" />
                          <span className="text-amber-400 text-xs font-medium">{testimonial.rating}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Quote */}
                  <div className="relative mb-4">
                    <Quote className="absolute -top-1 -left-1 text-gray-700" size={20} />
                    <p className="text-gray-300 text-sm pl-5 line-clamp-3 italic">
                      {testimonial.text}
                    </p>
                  </div>

                  {/* Package */}
                  {testimonial.package && (
                    <div className="mb-4">
                      <span className="px-3 py-1 bg-green-500/20 text-green-400 text-sm rounded-full">
                        {testimonial.package}
                      </span>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex gap-2 pt-3 border-t border-gray-700">
                    <button
                      onClick={() => openEditTestimonialModal(testimonial)}
                      className="flex-1 flex items-center justify-center gap-1 py-2 bg-blue-500/20 text-blue-400 rounded-lg text-sm hover:bg-blue-500/30 transition-colors"
                    >
                      <Edit size={14} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteTestimonial(testimonial.id)}
                      className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 transition-colors"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Testimonial Create/Edit Modal */}
      {showTestimonialModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">
                {editingTestimonial ? 'Edit Testimonial' : 'Add New Testimonial'}
              </h2>
              <button
                onClick={() => { setShowTestimonialModal(false); resetTestimonialForm(); }}
                className="p-2 text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>

            <form onSubmit={handleTestimonialSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-2">Name *</label>
                <input
                  type="text"
                  value={testimonialForm.name}
                  onChange={(e) => setTestimonialForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                  placeholder="Student name"
                  required
                  data-testid="testimonial-name-input"
                />
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Photo URL (optional)</label>
                <input
                  type="text"
                  value={testimonialForm.photo_url}
                  onChange={(e) => setTestimonialForm(f => ({ ...f, photo_url: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                  placeholder="https://..."
                  data-testid="testimonial-photo-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Course/Batch</label>
                  <input
                    type="text"
                    value={testimonialForm.course}
                    onChange={(e) => setTestimonialForm(f => ({ ...f, course: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    placeholder="AI Internship 2025"
                    data-testid="testimonial-course-input"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Current Role</label>
                  <input
                    type="text"
                    value={testimonialForm.role}
                    onChange={(e) => setTestimonialForm(f => ({ ...f, role: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    placeholder="SDE at Google"
                    data-testid="testimonial-role-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-400 mb-2">Testimonial Text *</label>
                <textarea
                  value={testimonialForm.text}
                  onChange={(e) => setTestimonialForm(f => ({ ...f, text: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                  placeholder="What the student said about the program..."
                  required
                  data-testid="testimonial-text-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Rating (optional)</label>
                  <select
                    value={testimonialForm.rating || ''}
                    onChange={(e) => setTestimonialForm(f => ({ ...f, rating: e.target.value ? parseFloat(e.target.value) : null }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    data-testid="testimonial-rating-input"
                  >
                    <option value="">No rating</option>
                    <option value="5">5 Stars</option>
                    <option value="4.5">4.5 Stars</option>
                    <option value="4">4 Stars</option>
                    <option value="3.5">3.5 Stars</option>
                    <option value="3">3 Stars</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Package Achieved</label>
                  <input
                    type="text"
                    value={testimonialForm.package}
                    onChange={(e) => setTestimonialForm(f => ({ ...f, package: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    placeholder="45 LPA"
                    data-testid="testimonial-package-input"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="testimonial_active"
                  checked={testimonialForm.is_active}
                  onChange={(e) => setTestimonialForm(f => ({ ...f, is_active: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-pink-500 focus:ring-pink-500"
                />
                <label htmlFor="testimonial_active" className="text-gray-300">
                  Active (display on landing page)
                </label>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => { setShowTestimonialModal(false); resetTestimonialForm(); }}
                  className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  {editingTestimonial ? 'Update Testimonial' : 'Add Testimonial'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Course Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-3xl my-8">
            <div className="flex items-center justify-between p-6 border-b border-gray-700">
              <h2 className="text-xl font-bold text-white">
                {editingCourse ? 'Edit Course' : 'Create New Course'}
              </h2>
              <button
                onClick={() => { setShowModal(false); resetForm(); }}
                className="p-2 text-gray-400 hover:text-white"
              >
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Title *</label>
                  <input
                    type="text"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm(f => ({ ...f, title: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    placeholder="Course title"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Slug *</label>
                  <input
                    type="text"
                    value={courseForm.slug}
                    onChange={(e) => setCourseForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    placeholder="course-slug"
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Short Description</label>
                <textarea
                  value={courseForm.description}
                  onChange={(e) => setCourseForm(f => ({ ...f, description: e.target.value }))}
                  rows={2}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                  placeholder="Brief description..."
                />
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Category</label>
                  <select
                    value={courseForm.category}
                    onChange={(e) => setCourseForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                  >
                    <option value="summer_internship">Summer Internship</option>
                    <option value="placement_accelerator">Placement Accelerator</option>
                    <option value="upskilling">Upskilling</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Mode</label>
                  <select
                    value={courseForm.mode}
                    onChange={(e) => setCourseForm(f => ({ ...f, mode: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                  >
                    <option value="online">Online</option>
                    <option value="offline">Offline</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Status</label>
                  <select
                    value={courseForm.status}
                    onChange={(e) => setCourseForm(f => ({ ...f, status: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                  >
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Price (₹)</label>
                  <input
                    type="number"
                    value={courseForm.price}
                    onChange={(e) => setCourseForm(f => ({ ...f, price: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Original Price (₹)</label>
                  <input
                    type="number"
                    value={courseForm.original_price}
                    onChange={(e) => setCourseForm(f => ({ ...f, original_price: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Duration (weeks)</label>
                  <input
                    type="number"
                    value={courseForm.duration_weeks}
                    onChange={(e) => setCourseForm(f => ({ ...f, duration_weeks: parseInt(e.target.value) || 0 }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    min="1"
                  />
                </div>
              </div>
              
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Start Date</label>
                  <input
                    type="date"
                    value={courseForm.start_date}
                    onChange={(e) => setCourseForm(f => ({ ...f, start_date: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">End Date</label>
                  <input
                    type="date"
                    value={courseForm.end_date}
                    onChange={(e) => setCourseForm(f => ({ ...f, end_date: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Max Seats</label>
                  <input
                    type="number"
                    value={courseForm.max_seats}
                    onChange={(e) => setCourseForm(f => ({ ...f, max_seats: parseInt(e.target.value) || 50 }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    min="1"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-2">Venue (for offline/hybrid)</label>
                <input
                  type="text"
                  value={courseForm.venue}
                  onChange={(e) => setCourseForm(f => ({ ...f, venue: e.target.value }))}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                  placeholder="Location or venue name"
                />
              </div>
              
              {/* Venue Details Section - Required for Offline Mode */}
              {(courseForm.mode === 'offline' || courseForm.mode === 'hybrid') && (
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl space-y-4">
                  <div className="flex items-center gap-2 text-amber-400 font-medium">
                    <MapPin size={18} />
                    Venue Details {courseForm.mode === 'offline' && <span className="text-red-400">*</span>}
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        Venue Name {courseForm.mode === 'offline' && <span className="text-red-400">*</span>}
                      </label>
                      <input
                        type="text"
                        value={courseForm.venue_name}
                        onChange={(e) => setCourseForm(f => ({ ...f, venue_name: e.target.value }))}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
                        placeholder="e.g., IIT Gandhinagar Campus"
                        required={courseForm.mode === 'offline'}
                        data-testid="venue-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">
                        Venue Address {courseForm.mode === 'offline' && <span className="text-red-400">*</span>}
                      </label>
                      <input
                        type="text"
                        value={courseForm.venue_address}
                        onChange={(e) => setCourseForm(f => ({ ...f, venue_address: e.target.value }))}
                        className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-amber-500"
                        placeholder="Full address with city, state, pincode"
                        required={courseForm.mode === 'offline'}
                        data-testid="venue-address-input"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Coupon Codes Section */}
              <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-purple-400 font-medium">
                    <Tag size={18} />
                    Coupon Codes
                  </div>
                </div>
                
                {/* Add New Coupon */}
                <div className="grid md:grid-cols-4 gap-3">
                  <input
                    type="text"
                    value={newCoupon.code}
                    onChange={(e) => setNewCoupon(c => ({ ...c, code: e.target.value.toUpperCase() }))}
                    className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm"
                    placeholder="COUPON CODE"
                    data-testid="coupon-code-input"
                  />
                  <select
                    value={newCoupon.discount_type}
                    onChange={(e) => setNewCoupon(c => ({ ...c, discount_type: e.target.value }))}
                    className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm"
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="fixed">Fixed Amount (₹)</option>
                  </select>
                  <input
                    type="number"
                    value={newCoupon.discount_value}
                    onChange={(e) => setNewCoupon(c => ({ ...c, discount_value: parseFloat(e.target.value) || 0 }))}
                    className="px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 text-sm"
                    placeholder="Value"
                    min="0"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (newCoupon.code.trim()) {
                        setCourseForm(f => ({
                          ...f,
                          coupons: [...f.coupons, { ...newCoupon, code: newCoupon.code.trim().toUpperCase() }]
                        }));
                        setNewCoupon({ code: '', discount_type: 'percentage', discount_value: 10, is_active: true });
                      }
                    }}
                    className="px-3 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 text-sm font-medium"
                  >
                    Add Coupon
                  </button>
                </div>
                
                {/* Existing Coupons */}
                {courseForm.coupons && courseForm.coupons.length > 0 && (
                  <div className="space-y-2">
                    {courseForm.coupons.map((coupon, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-gray-800/50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded font-mono text-sm">
                            {coupon.code}
                          </span>
                          <span className="text-gray-400 text-sm">
                            {coupon.discount_type === 'percentage' 
                              ? `${coupon.discount_value}% off` 
                              : `₹${coupon.discount_value} off`}
                          </span>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={coupon.is_active}
                              onChange={(e) => {
                                const updatedCoupons = [...courseForm.coupons];
                                updatedCoupons[idx].is_active = e.target.checked;
                                setCourseForm(f => ({ ...f, coupons: updatedCoupons }));
                              }}
                              className="w-4 h-4 rounded border-gray-700 bg-gray-800 text-purple-500"
                            />
                            <span className={`text-xs ${coupon.is_active ? 'text-green-400' : 'text-gray-500'}`}>
                              {coupon.is_active ? 'Active' : 'Disabled'}
                            </span>
                          </label>
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            setCourseForm(f => ({
                              ...f,
                              coupons: f.coupons.filter((_, i) => i !== idx)
                            }));
                          }}
                          className="p-1 text-gray-500 hover:text-red-400"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Mentor/Instructor Section (Optional) */}
              <div className="p-4 bg-cyan-500/10 border border-cyan-500/30 rounded-xl space-y-4">
                <div className="flex items-center gap-2 text-cyan-400 font-medium">
                  <User size={18} />
                  Mentor / Instructor (Optional)
                </div>
                <p className="text-gray-500 text-sm">Add mentor details to display on the course page. Leave empty to hide this section.</p>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Mentor Name</label>
                    <input
                      type="text"
                      value={mentorForm.name}
                      onChange={(e) => setMentorForm(m => ({ ...m, name: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      placeholder="Dr. John Smith"
                      data-testid="mentor-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Profile Image URL</label>
                    <input
                      type="text"
                      value={mentorForm.profile_image}
                      onChange={(e) => setMentorForm(m => ({ ...m, profile_image: e.target.value }))}
                      className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      placeholder="https://..."
                      data-testid="mentor-image-input"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2">Work Experience (one line)</label>
                  <input
                    type="text"
                    value={mentorForm.experience}
                    onChange={(e) => setMentorForm(m => ({ ...m, experience: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    placeholder="e.g., 15+ years in AI/ML at Google, Microsoft"
                    data-testid="mentor-experience-input"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-gray-400 mb-2 flex items-center gap-2">
                    <Linkedin size={14} />
                    LinkedIn Profile URL
                  </label>
                  <input
                    type="url"
                    value={mentorForm.linkedin_url}
                    onChange={(e) => setMentorForm(m => ({ ...m, linkedin_url: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    placeholder="https://linkedin.com/in/username"
                    data-testid="mentor-linkedin-input"
                  />
                </div>
                
                {/* Preview */}
                {mentorForm.name && (
                  <div className="p-3 bg-gray-800/50 rounded-lg">
                    <p className="text-xs text-gray-500 mb-2">Preview:</p>
                    <div className="flex items-center gap-3">
                      {mentorForm.profile_image ? (
                        <img src={mentorForm.profile_image} alt={mentorForm.name} className="w-12 h-12 rounded-full object-cover" />
                      ) : (
                        <div className="w-12 h-12 bg-cyan-500/20 rounded-full flex items-center justify-center text-cyan-400 font-bold">
                          {mentorForm.name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <p className="text-white font-medium">{mentorForm.name}</p>
                        {mentorForm.experience && <p className="text-gray-400 text-sm">{mentorForm.experience}</p>}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Learning Outcomes */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Learning Outcomes</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newOutcome}
                    onChange={(e) => setNewOutcome(e.target.value)}
                    className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    placeholder="Add learning outcome..."
                  />
                  <button
                    type="button"
                    onClick={() => addToList('learning_outcomes', newOutcome, setNewOutcome)}
                    className="px-4 py-2 bg-pink-500/20 text-pink-400 rounded-lg hover:bg-pink-500/30"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {courseForm.learning_outcomes.map((outcome, idx) => (
                    <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-gray-800 text-gray-300 rounded-full text-sm">
                      {outcome}
                      <button type="button" onClick={() => removeFromList('learning_outcomes', idx)}>
                        <X size={14} className="text-gray-500 hover:text-red-400" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Skills */}
              <div>
                <label className="block text-sm text-gray-400 mb-2">Skills</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    className="flex-1 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-pink-500"
                    placeholder="Add skill..."
                  />
                  <button
                    type="button"
                    onClick={() => addToList('skills', newSkill, setNewSkill)}
                    className="px-4 py-2 bg-pink-500/20 text-pink-400 rounded-lg hover:bg-pink-500/30"
                  >
                    <Plus size={18} />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {courseForm.skills.map((skill, idx) => (
                    <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-cyan-500/20 text-cyan-400 rounded-full text-sm">
                      {skill}
                      <button type="button" onClick={() => removeFromList('skills', idx)}>
                        <X size={14} className="text-gray-500 hover:text-red-400" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
              
              {/* ============ NEW SECTIONS ============ */}
              
              {/* Projects Section */}
              <div className="border-t border-gray-700 pt-6">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Briefcase size={18} className="text-purple-400" />
                  Industry Projects
                </h4>
                <p className="text-gray-400 text-sm mb-4">Add projects that students can choose during enrollment</p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Project Title</label>
                    <input
                      type="text"
                      id="project-title-input"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      placeholder="E.g., AI Chatbot Development"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Company/Partner</label>
                    <input
                      type="text"
                      id="project-company-input"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                      placeholder="E.g., Microsoft"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-2">Project Description</label>
                  <textarea
                    id="project-desc-input"
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500 h-20"
                    placeholder="Brief description of the project..."
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const title = document.getElementById('project-title-input')?.value;
                    const company = document.getElementById('project-company-input')?.value;
                    const desc = document.getElementById('project-desc-input')?.value;
                    if (title) {
                      setCourseForm(f => ({
                        ...f,
                        projects: [...(f.projects || []), { title, company, description: desc, id: Date.now() }]
                      }));
                      document.getElementById('project-title-input').value = '';
                      document.getElementById('project-company-input').value = '';
                      document.getElementById('project-desc-input').value = '';
                    }
                  }}
                  className="px-4 py-2 bg-purple-500/20 text-purple-400 rounded-lg hover:bg-purple-500/30 flex items-center gap-2"
                >
                  <Plus size={16} /> Add Project
                </button>
                
                {courseForm.projects?.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {courseForm.projects.map((proj, idx) => (
                      <div key={idx} className="p-3 bg-gray-800/50 rounded-lg flex justify-between items-start">
                        <div>
                          <p className="text-white font-medium">{proj.title}</p>
                          {proj.company && <p className="text-purple-400 text-sm">{proj.company}</p>}
                          {proj.description && <p className="text-gray-400 text-sm mt-1">{proj.description}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCourseForm(f => ({
                            ...f,
                            projects: f.projects.filter((_, i) => i !== idx)
                          }))}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="mt-4">
                  <label className="block text-sm text-gray-400 mb-2">Min Projects Required (during enrollment)</label>
                  <input
                    type="number"
                    value={courseForm.min_projects_required || 2}
                    onChange={(e) => setCourseForm(f => ({ ...f, min_projects_required: parseInt(e.target.value) || 2 }))}
                    className="w-24 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-purple-500"
                    min="1"
                  />
                </div>
              </div>
              
              {/* Industry Partners Section */}
              <div className="border-t border-gray-700 pt-6">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Briefcase size={18} className="text-cyan-400" />
                  Industry Partners (Optional)
                </h4>
                <p className="text-gray-400 text-sm mb-4">Add partner company logos and certifications</p>
                
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Partner Name</label>
                    <input
                      type="text"
                      id="partner-name-input"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      placeholder="E.g., Google"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Logo URL</label>
                    <input
                      type="text"
                      id="partner-logo-input"
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                      placeholder="https://..."
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm text-gray-400 mb-2">Certification Name (if any)</label>
                  <input
                    type="text"
                    id="partner-cert-input"
                    className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-cyan-500"
                    placeholder="E.g., Google Cloud Professional Certificate"
                  />
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const name = document.getElementById('partner-name-input')?.value;
                    const logo = document.getElementById('partner-logo-input')?.value;
                    const cert = document.getElementById('partner-cert-input')?.value;
                    if (name) {
                      setCourseForm(f => ({
                        ...f,
                        industry_partners: [...(f.industry_partners || []), { name, logo_url: logo, certification: cert, id: Date.now() }]
                      }));
                      document.getElementById('partner-name-input').value = '';
                      document.getElementById('partner-logo-input').value = '';
                      document.getElementById('partner-cert-input').value = '';
                    }
                  }}
                  className="px-4 py-2 bg-cyan-500/20 text-cyan-400 rounded-lg hover:bg-cyan-500/30 flex items-center gap-2"
                >
                  <Plus size={16} /> Add Partner
                </button>
                
                {courseForm.industry_partners?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-3">
                    {courseForm.industry_partners.map((partner, idx) => (
                      <div key={idx} className="p-3 bg-gray-800/50 rounded-lg flex items-center gap-3">
                        {partner.logo_url && (
                          <img src={partner.logo_url} alt={partner.name} className="w-10 h-10 object-contain rounded" />
                        )}
                        <div>
                          <p className="text-white font-medium text-sm">{partner.name}</p>
                          {partner.certification && <p className="text-cyan-400 text-xs">{partner.certification}</p>}
                        </div>
                        <button
                          type="button"
                          onClick={() => setCourseForm(f => ({
                            ...f,
                            industry_partners: f.industry_partners.filter((_, i) => i !== idx)
                          }))}
                          className="text-red-400 hover:text-red-300 ml-2"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Certificates Section */}
              <div className="border-t border-gray-700 pt-6">
                <h4 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Star size={18} className="text-yellow-400" />
                  Certificates (Optional)
                </h4>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Course Certificate Title</label>
                    <input
                      type="text"
                      value={courseForm.course_certificate?.title || ''}
                      onChange={(e) => setCourseForm(f => ({
                        ...f,
                        course_certificate: { ...(f.course_certificate || {}), title: e.target.value }
                      }))}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                      placeholder="Certificate of Completion - Future Skills Program"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Certificate Preview Image URL</label>
                    <input
                      type="text"
                      value={courseForm.course_certificate?.preview_image || ''}
                      onChange={(e) => setCourseForm(f => ({
                        ...f,
                        course_certificate: { ...(f.course_certificate || {}), preview_image: e.target.value }
                      }))}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                      placeholder="https://... (preview image of certificate)"
                    />
                  </div>
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">Confirmation Letter Preview URL</label>
                    <input
                      type="text"
                      value={courseForm.confirmation_letter_preview || ''}
                      onChange={(e) => setCourseForm(f => ({ ...f, confirmation_letter_preview: e.target.value }))}
                      className="w-full px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
                      placeholder="https://... (demo image of confirmation letter)"
                    />
                  </div>
                </div>
              </div>
              
              {/* Impact Metrics Toggle */}
              <div className="border-t border-gray-700 pt-6">
                <div className="flex items-center gap-3 mb-4">
                  <input
                    type="checkbox"
                    id="show_impact_metrics"
                    checked={courseForm.show_impact_metrics !== false}
                    onChange={(e) => setCourseForm(f => ({ ...f, show_impact_metrics: e.target.checked }))}
                    className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-green-500 focus:ring-green-500"
                  />
                  <label htmlFor="show_impact_metrics" className="text-white flex items-center gap-2">
                    <TrendingUp size={18} className="text-green-400" />
                    Show Impact Metrics Section
                  </label>
                </div>
                <p className="text-gray-400 text-sm">Displays career outcome statistics on the course page</p>
              </div>
              
              {/* Featured Toggle */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_featured"
                  checked={courseForm.is_featured}
                  onChange={(e) => setCourseForm(f => ({ ...f, is_featured: e.target.checked }))}
                  className="w-5 h-5 rounded border-gray-700 bg-gray-800 text-pink-500 focus:ring-pink-500"
                />
                <label htmlFor="is_featured" className="text-gray-300">Mark as Featured</label>
              </div>
              
              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-600 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
                >
                  {editingCourse ? 'Update Course' : 'Create Course'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FutureSkillsAdmin;
